import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import RatingStars from './RatingStars';
import { Star, Calendar, CheckCircle } from 'lucide-react';

const ReviewModal = ({ isOpen, onClose, friend, onSubmit }) => {
  const [reviewData, setReviewData] = useState({
    service_used: '',
    rating: 0,
    review: '',
    would_recommend: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (reviewData.rating === 0 || !reviewData.service_used) return;
    
    await onSubmit({
      ...reviewData,
      friend_id: friend.id
    });
    
    // Reset form
    setReviewData({
      service_used: '',
      rating: 0,
      review: '',
      would_recommend: true
    });
    
    onClose();
  };

  const handleInputChange = (field, value) => {
    setReviewData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!friend) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5" style={{color: 'var(--brand-tan)'}} />
            Review {friend.name}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="service">Service Used *</Label>
            <Select onValueChange={(value) => handleInputChange('service_used', value)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select service..." />
              </SelectTrigger>
              <SelectContent>
                {friend.services?.map(service => (
                  <SelectItem key={service} value={service}>
                    {service}
                  </SelectItem>
                ))}
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Rating *</Label>
            <div className="mt-2">
              <RatingStars 
                rating={reviewData.rating}
                onRatingChange={(rating) => handleInputChange('rating', rating)}
                interactive={true}
                size="w-8 h-8"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="review">Review (Optional)</Label>
            <Textarea
              id="review"
              value={reviewData.review}
              onChange={(e) => handleInputChange('review', e.target.value)}
              placeholder="Share your experience..."
              className="mt-1"
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="recommend"
              checked={reviewData.would_recommend}
              onChange={(e) => handleInputChange('would_recommend', e.target.checked)}
              className="rounded"
            />
            <Label htmlFor="recommend" className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" style={{color: 'var(--brand-navy)'}} />
              Would recommend to others
            </Label>
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={reviewData.rating === 0 || !reviewData.service_used}
              className="flex-1 text-white"
              style={{
                background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-dark) 100%)'
              }}
            >
              Submit Review
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;